import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';

const StatusPage: React.FC = () => {
  const { t } = useTranslation();
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const services = [
    {
      name: t('status.services.mainSite'),
      status: 'operational',
      uptime: '99.9%',
      responseTime: '120ms',
      icon: <CloudIcon />,
      description: t('status.services.mainSiteDesc'),
    },
    {
      name: t('status.services.api'),
      status: 'operational',
      uptime: '99.8%',
      responseTime: '85ms',
      icon: <SpeedIcon />,
      description: t('status.services.apiDesc'),
    },
    {
      name: t('status.services.database'),
      status: 'operational',
      uptime: '99.95%',
      responseTime: '45ms',
      icon: <StorageIcon />,
      description: t('status.services.databaseDesc'),
    },
    {
      name: t('status.services.payments'),
      status: 'operational',
      uptime: '99.7%',
      responseTime: '200ms',
      icon: <SecurityIcon />,
      description: t('status.services.paymentsDesc'),
    },
    {
      name: t('status.services.email'),
      status: 'degraded',
      uptime: '98.5%',
      responseTime: '500ms',
      icon: <WarningIcon />,
      description: t('status.services.emailDesc'),
    },
    {
      name: t('status.services.cdn'),
      status: 'operational',
      uptime: '99.9%',
      responseTime: '50ms',
      icon: <SpeedIcon />,
      description: t('status.services.cdnDesc'),
    },
  ];

  const incidents = [
    {
      id: 1,
      title: t('status.incidents.emailDelay.title'),
      status: 'investigating',
      impact: 'minor',
      description: t('status.incidents.emailDelay.description'),
      time: '2 hours ago',
    },
    {
      id: 2,
      title: t('status.incidents.maintenance.title'),
      status: 'resolved',
      impact: 'major',
      description: t('status.incidents.maintenance.description'),
      time: '1 day ago',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return '#10b981';
      case 'degraded':
        return '#f59e0b';
      case 'outage':
        return '#ef4444';
      case 'maintenance':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon />;
      case 'degraded':
        return <WarningIcon />;
      case 'outage':
        return <ErrorIcon />;
      case 'maintenance':
        return <ScheduleIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'minor':
        return '#f59e0b';
      case 'major':
        return '#ef4444';
      case 'critical':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const refreshStatus = () => {
    setLastUpdated(new Date());
  };

  const overallStatus = services.every(service => service.status === 'operational') 
    ? 'operational' 
    : services.some(service => service.status === 'outage') 
    ? 'outage' 
    : 'degraded';

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('status.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
            {t('status.subtitle')}
          </Typography>
        </Box>

        {/* Overall Status */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    color: getStatusColor(overallStatus),
                    mr: 2,
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: 32, md: 40 },
                    },
                  }}
                >
                  {getStatusIcon(overallStatus)}
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    {t('status.overallStatus')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: getStatusColor(overallStatus),
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      fontWeight: 600,
                    }}
                  >
                    {t(`status.statuses.${overallStatus}`)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#a0a0a0',
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                  }}
                >
                  {t('status.lastUpdated')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#ffffff',
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                    fontWeight: 600,
                  }}
                >
                  {lastUpdated.toLocaleTimeString('bg-BG')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Chip
                icon={<RefreshIcon />}
                label={t('status.refresh')}
                onClick={refreshStatus}
                sx={{
                  backgroundColor: 'rgba(40, 160, 240, 0.2)',
                  color: '#28A0F0',
                  border: '1px solid #28A0F0',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(40, 160, 240, 0.3)',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Services Status */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {t('status.services.title')}
          </Typography>
          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 3,
                    p: 3,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(40, 160, 240, 0.2)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        color: getStatusColor(service.status),
                        mr: 2,
                        '& .MuiSvgIcon-root': {
                          fontSize: { xs: 24, md: 28 },
                        },
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                        }}
                      >
                        {service.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#a0a0a0',
                          fontSize: { xs: '0.8rem', md: '0.875rem' },
                        }}
                      >
                        {service.description}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        color: getStatusColor(service.status),
                        '& .MuiSvgIcon-root': {
                          fontSize: { xs: 20, md: 24 },
                        },
                      }}
                    >
                      {getStatusIcon(service.status)}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#a0a0a0',
                          fontSize: { xs: '0.8rem', md: '0.875rem' },
                        }}
                      >
                        {t('status.uptime')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: { xs: '0.9rem', md: '1rem' },
                        }}
                      >
                        {service.uptime}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#a0a0a0',
                          fontSize: { xs: '0.8rem', md: '0.875rem' },
                        }}
                      >
                        {t('status.responseTime')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: { xs: '0.9rem', md: '1rem' },
                        }}
                      >
                        {service.responseTime}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Recent Incidents */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {t('status.incidents.title')}
          </Typography>
          {incidents.length === 0 ? (
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(40, 160, 240, 0.1) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  color: '#ffffff',
                  mb: 1,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                {t('status.incidents.noIncidents')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#a0a0a0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                {t('status.incidents.noIncidentsDesc')}
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {incidents.map((incident, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: 3,
                      p: 3,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          color: getStatusColor(incident.status),
                          mr: 2,
                          mt: 0.5,
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: 20, md: 24 },
                          },
                        }}
                      >
                        {getStatusIcon(incident.status)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#ffffff',
                              fontWeight: 600,
                              fontSize: { xs: '1rem', md: '1.25rem' },
                              mr: 2,
                            }}
                          >
                            {incident.title}
                          </Typography>
                          <Chip
                            label={t(`status.impacts.${incident.impact}`)}
                            size="small"
                            sx={{
                              backgroundColor: `${getImpactColor(incident.impact)}20`,
                              color: getImpactColor(incident.impact),
                              border: `1px solid ${getImpactColor(incident.impact)}`,
                              fontSize: { xs: '0.7rem', md: '0.75rem' },
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a0a0a0',
                            mb: 1,
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                          }}
                        >
                          {incident.description}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6b7280',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                          }}
                        >
                          {incident.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Status History */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(40, 160, 240, 0.3)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
              {t('status.history.title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('status.history.description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                {t('status.history.last30Days')}: 99.9%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                {t('status.history.last90Days')}: 99.8%
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </Box>
  );
};

export default StatusPage;
